import { useTranslation } from "react-i18next"

import type { SkillPackageSummary } from "@/api/admin/skill-packages"

import { AlertTriangle } from "@/components/icons"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Spinner } from "@/components/ui/spinner"

type DeleteSkillPackageDialogProps = {
  /** The package to delete; null keeps the dialog closed. */
  pkg: SkillPackageSummary | null
  isPending: boolean
  onOpenChange: (open: boolean) => void
  /** Resolves when the delete finished (successfully or not — the card shows the error). */
  onConfirm: () => Promise<void>
}

/**
 * Delete confirmation for a skill package (BUG-014).
 *
 * Deleting removes the package head AND every published version, and there is
 * no undo — a published version is what agents install, and a draft can hold
 * several sittings of (AI-assisted) authoring. So every delete is confirmed;
 * the wording names what is lost for a published package.
 */
export function DeleteSkillPackageDialog({
  pkg,
  isPending,
  onOpenChange,
  onConfirm,
}: DeleteSkillPackageDialogProps) {
  const { t } = useTranslation()
  const published = (pkg?.latest_version ?? 0) > 0

  return (
    <AlertDialog open={pkg !== null} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-lg" data-testid="skill-package-delete-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="text-foreground size-5" />
            {t("adminDashboard.skillPackages.deleteDialog.title", {
              defaultValue: "Delete skill package?",
            })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {published
              ? t("adminDashboard.skillPackages.deleteDialog.publishedDescription", {
                  defaultValue:
                    "“{{name}}” is published (latest v{{version}}). Deleting it removes every published version and the draft for good — this cannot be undone. Agents that have it installed must uninstall it first.",
                  name: pkg?.name ?? "",
                  version: pkg?.latest_version ?? 0,
                })
              : t("adminDashboard.skillPackages.deleteDialog.draftDescription", {
                  defaultValue:
                    "Deleting the draft “{{name}}” removes it for good — this cannot be undone.",
                  name: pkg?.name ?? "",
                })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>
            {t("adminDashboard.skillPackages.deleteDialog.cancel", { defaultValue: "Cancel" })}
          </AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            data-testid="skill-package-delete-confirm"
            disabled={isPending}
            onClick={(event) => {
              // Keep the dialog open until the request settles; the owner closes it.
              event.preventDefault()
              void onConfirm()
            }}
          >
            {isPending ? (
              <>
                <Spinner />
                {t("adminDashboard.skillPackages.deleteDialog.deleting", {
                  defaultValue: "Deleting…",
                })}
              </>
            ) : (
              t("adminDashboard.skillPackages.deleteDialog.confirm", {
                defaultValue: "Delete package",
              })
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
